import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
  OnDestroy,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** Above modal stacks; matches date-range-picker overlay. */
const OVERLAY_Z_INDEX = 11002;
const PANEL_ESTIMATED_HEIGHT_PX = 240;

@Component({
  selector: 'app-time-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './time-picker.component.html',
  styleUrl: './time-picker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimePickerComponent),
      multi: true,
    },
  ],
})
export class TimePickerComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  @Input() placeholder = 'Select time';
  @Input() styleClass = '';
  @Input() disabled = false;
  @Input() minuteStep = 5;
  @Input() openAbove = false;
  /** Optional minimum time in HH:mm (24h). Values below this are not allowed. */
  @Input() minTime = '';

  @Output() onTimeChange = new EventEmitter<string>();
  @Output() onOpen = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();

  value = '';
  isOpen = false;
  selectedHour: number | null = null;
  selectedMinute: number | null = null;
  hourInput = '';
  minuteInput = '';

  private dropdownOriginalParent: HTMLElement | null = null;
  private scrollListener: (() => void) | null = null;
  private positionRetryCount = 0;
  private readonly docClickHandler = (event: MouseEvent) => this.onDocumentClick(event);

  @ViewChild('panelDropdown', { static: false }) panelDropdown?: ElementRef<HTMLDivElement>;
  @ViewChild('triggerElement', { static: false }) triggerElement?: ElementRef<HTMLDivElement>;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    document.addEventListener('click', this.docClickHandler, { capture: true });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['minTime'] && !changes['minTime'].firstChange) {
      this.refreshMinTimeConstraint();
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.docClickHandler, { capture: true });
    this.teardownPanel();
  }

  /** Capture phase — same pattern as date-range-picker; avoids fighting trigger toggle. */
  private onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) {
      return;
    }
    const target = event.target as HTMLElement;
    const inTrigger = this.elementRef.nativeElement.contains(target);
    const inPanel = this.panelDropdown?.nativeElement?.contains(target);
    if (!inTrigger && !inPanel) {
      this.closeDropdown();
    }
  }

  get displayValue(): string {
    return this.value || '';
  }

  get previewTime(): string {
    if (this.selectedHour === null || this.selectedMinute === null) {
      return '--:--';
    }
    return `${this.formatHour(this.selectedHour)}:${this.formatMinute(this.selectedMinute)}`;
  }

  get canApply(): boolean {
    if (this.selectedHour === null || this.selectedMinute === null) {
      return false;
    }
    return !this.isSelectionBelowMinTime();
  }

  get canDecrementHour(): boolean {
    const minMinutes = this.getMinTimeMinutes();
    if (minMinutes === null) {
      return true;
    }
    return this.getTimeMinutesAfterHourDecrement() >= minMinutes;
  }

  get canDecrementMinute(): boolean {
    const minMinutes = this.getMinTimeMinutes();
    if (minMinutes === null) {
      return true;
    }
    return this.getTimeMinutesAfterMinuteDecrement() >= minMinutes;
  }

  toggleDropdown(event?: Event): void {
    if (this.disabled) {
      return;
    }
    event?.stopPropagation();
    if (this.isOpen) {
      this.closeDropdown();
      return;
    }
    this.openDropdown();
  }

  closeDropdown(): void {
    if (!this.isOpen) {
      return;
    }
    this.teardownPanel();
    this.isOpen = false;
    this.onClose.emit();
    this.cdr.markForCheck();
  }

  private openDropdown(): void {
    if (this.disabled) {
      return;
    }
    this.positionRetryCount = 0;
    this.ensureSelectionDefaults();
    this.refreshMinTimeConstraint();
    this.onTouched();
    this.isOpen = true;
    this.onOpen.emit();
    this.cdr.detectChanges();
    this.schedulePositionPanel();
  }

  /** Re-apply minTime after parent updates (e.g. expiry date set to today). */
  refreshMinTimeConstraint(): void {
    this.enforceMinTimeBound();
    this.syncInputsFromSelection();
    this.cdr.markForCheck();
  }

  /** Wait for *ngIf panel + ViewChild before measuring trigger rect. */
  private schedulePositionPanel(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.isOpen) {
          return;
        }
        this.positionPanel();
        this.cdr.markForCheck();
      });
    });
  }

  incrementHour(): void {
    const base = this.selectedHour ?? 0;
    this.selectedHour = (base + 1) % 24;
    this.ensureMinuteSelected();
    this.enforceMinTimeBound();
    this.syncInputsFromSelection();
    this.cdr.markForCheck();
  }

  decrementHour(): void {
    if (!this.canDecrementHour) {
      return;
    }
    const base = this.selectedHour ?? 0;
    this.selectedHour = (base + 23) % 24;
    this.ensureMinuteSelected();
    this.enforceMinTimeBound();
    this.syncInputsFromSelection();
    this.cdr.markForCheck();
  }

  incrementMinute(): void {
    const step = this.getMinuteStep();
    const base = this.selectedMinute ?? 0;
    let next = base + step;
    if (next >= 60) {
      next = 0;
      this.selectedHour = ((this.selectedHour ?? 0) + 1) % 24;
    }
    this.selectedMinute = next;
    this.ensureHourSelected();
    this.enforceMinTimeBound();
    this.syncInputsFromSelection();
    this.cdr.markForCheck();
  }

  decrementMinute(): void {
    if (!this.canDecrementMinute) {
      return;
    }
    const step = this.getMinuteStep();
    const base = this.selectedMinute ?? 0;
    let next = base - step;
    if (next < 0) {
      next = 60 - step;
      this.selectedHour = ((this.selectedHour ?? 0) + 23) % 24;
    }
    this.selectedMinute = next;
    this.ensureHourSelected();
    this.enforceMinTimeBound();
    this.syncInputsFromSelection();
    this.cdr.markForCheck();
  }

  onHourInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    const digits = el.value.replace(/\D/g, '').slice(0, 2);
    el.value = digits;
    this.hourInput = digits;
    if (!digits) {
      this.selectedHour = null;
      this.cdr.markForCheck();
      return;
    }
    const parsed = Number(digits);
    if (Number.isNaN(parsed)) {
      return;
    }
    this.selectedHour = Math.min(23, parsed);
    this.enforceMinTimeBound();
    this.hourInput = digits.length === 2 ? this.formatHour(this.selectedHour) : digits;
    this.ensureMinuteSelected();
    this.cdr.markForCheck();
  }

  onHourBlur(): void {
    if (!this.hourInput.trim()) {
      this.selectedHour = 0;
    } else {
      const parsed = Number(this.hourInput);
      this.selectedHour = Math.min(23, Math.max(0, Number.isNaN(parsed) ? 0 : parsed));
    }
    this.hourInput = this.formatHour(this.selectedHour);
    this.enforceMinTimeBound();
    this.syncInputsFromSelection();
    this.cdr.markForCheck();
  }

  onMinuteInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    const digits = el.value.replace(/\D/g, '').slice(0, 2);
    el.value = digits;
    this.minuteInput = digits;
    if (!digits) {
      this.selectedMinute = null;
      this.cdr.markForCheck();
      return;
    }
    const parsed = Number(digits);
    if (Number.isNaN(parsed)) {
      return;
    }
    this.selectedMinute = Math.min(59, parsed);
    this.enforceMinTimeBound();
    this.minuteInput = digits.length === 2 ? this.formatMinute(this.selectedMinute) : digits;
    this.ensureHourSelected();
    this.cdr.markForCheck();
  }

  onMinuteBlur(): void {
    if (!this.minuteInput.trim()) {
      this.selectedMinute = 0;
    } else {
      const parsed = Number(this.minuteInput);
      this.selectedMinute = this.snapMinute(Math.min(59, Math.max(0, Number.isNaN(parsed) ? 0 : parsed)));
    }
    this.minuteInput = this.formatMinute(this.selectedMinute);
    this.enforceMinTimeBound();
    this.syncInputsFromSelection();
    this.cdr.markForCheck();
  }

  setNow(): void {
    const now = new Date();
    this.selectedHour = now.getHours();
    this.selectedMinute = this.snapMinute(now.getMinutes());
    this.enforceMinTimeBound();
    this.syncInputsFromSelection();
    this.commitValue(true);
    this.cdr.markForCheck();
  }

  applyAndClose(): void {
    if (this.disabled) {
      return;
    }
    this.onHourBlur();
    this.onMinuteBlur();
    if (!this.canApply) {
      return;
    }
    this.commitValue(true);
  }

  clear(event?: MouseEvent): void {
    event?.stopPropagation();
    this.selectedHour = null;
    this.selectedMinute = null;
    this.hourInput = '';
    this.minuteInput = '';
    this.value = '';
    this.onChange('');
    this.onTimeChange.emit('');
    this.onTouched();
    this.closeDropdown();
  }

  onClearClick(event: MouseEvent): void {
    event.stopPropagation();
    this.clear();
  }

  formatHour(hour: number): string {
    return String(hour).padStart(2, '0');
  }

  formatMinute(minute: number): string {
    return String(minute).padStart(2, '0');
  }

  writeValue(value: string | null): void {
    this.value = value?.trim() ?? '';
    this.parseValue(this.value);
    this.enforceMinTimeBound();
    this.syncInputsFromSelection();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.closeDropdown();
    }
  }

  private ensureSelectionDefaults(): void {
    if (this.selectedHour === null && this.selectedMinute === null) {
      const now = new Date();
      this.selectedHour = now.getHours();
      this.selectedMinute = this.snapMinute(now.getMinutes());
      this.enforceMinTimeBound();
      this.syncInputsFromSelection();
      return;
    }
    this.ensureHourSelected();
    this.ensureMinuteSelected();
    this.enforceMinTimeBound();
    this.syncInputsFromSelection();
  }

  private syncInputsFromSelection(): void {
    this.hourInput =
      this.selectedHour !== null ? this.formatHour(this.selectedHour) : '';
    this.minuteInput =
      this.selectedMinute !== null ? this.formatMinute(this.selectedMinute) : '';
  }

  private ensureHourSelected(): void {
    if (this.selectedHour === null) {
      this.selectedHour = 0;
    }
  }

  private ensureMinuteSelected(): void {
    if (this.selectedMinute === null) {
      this.selectedMinute = 0;
    }
  }

  private parseValue(raw: string): void {
    const match = /^(\d{1,2}):(\d{2})$/.exec(raw);
    if (!match) {
      this.selectedHour = null;
      this.selectedMinute = null;
      this.hourInput = '';
      this.minuteInput = '';
      return;
    }
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (hour >= 0 && hour < 24 && minute >= 0 && minute < 60) {
      this.selectedHour = hour;
      this.selectedMinute = this.snapMinute(minute);
      this.enforceMinTimeBound();
      this.syncInputsFromSelection();
    } else {
      this.selectedHour = null;
      this.selectedMinute = null;
      this.hourInput = '';
      this.minuteInput = '';
    }
  }

  private getMinuteStep(): number {
    return Math.max(1, Math.min(30, this.minuteStep));
  }

  private snapMinute(minute: number): number {
    const step = this.getMinuteStep();
    const snapped = Math.round(minute / step) * step;
    return Math.min(59, Math.max(0, snapped));
  }

  private commitValue(closeOnComplete: boolean): void {
    if (this.selectedHour === null || this.selectedMinute === null) {
      return;
    }
    const next = `${this.formatHour(this.selectedHour)}:${this.formatMinute(this.selectedMinute)}`;
    if (this.isSelectionBelowMinTime()) {
      return;
    }
    if (this.value !== next) {
      this.value = next;
      this.onChange(next);
      this.onTimeChange.emit(next);
    }
    this.onTouched();
    if (closeOnComplete) {
      this.closeDropdown();
    }
  }

  private positionPanel(): void {
    if (!this.panelDropdown?.nativeElement || !this.triggerElement?.nativeElement) {
      if (this.positionRetryCount++ < 8) {
        this.schedulePositionPanel();
      }
      return;
    }

    const trigger = this.triggerElement.nativeElement;
    const dropdown = this.panelDropdown.nativeElement;
    const triggerRect = trigger.getBoundingClientRect();
    if (triggerRect.width === 0 && triggerRect.height === 0) {
      if (this.positionRetryCount++ < 8) {
        this.schedulePositionPanel();
      }
      return;
    }
    this.positionRetryCount = 0;

    const viewportW = window.innerWidth || document.documentElement.clientWidth;

    if (dropdown.parentNode !== document.body) {
      this.dropdownOriginalParent = dropdown.parentElement;
      document.body.appendChild(dropdown);
    }

    dropdown.classList.add('time-picker-panel-overlay');

    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const placeAbove =
      this.openAbove || (spaceBelow < PANEL_ESTIMATED_HEIGHT_PX && spaceAbove > spaceBelow);

    const width = Math.min(300, Math.max(260, triggerRect.width));
    // Right-align overlay with trigger; this keeps visual alignment stable
    // when panel width is larger than the input (e.g. narrow table cells).
    let left = triggerRect.right - width;
    if (left + width > viewportW - 8) {
      left = Math.max(8, viewportW - width - 8);
    }
    if (left < 8) {
      left = 8;
    }

    dropdown.style.position = 'fixed';
    dropdown.style.left = `${left}px`;
    dropdown.style.width = `${width}px`;
    dropdown.style.minWidth = `${width}px`;
    dropdown.style.maxWidth = `${width}px`;
    dropdown.style.zIndex = String(OVERLAY_Z_INDEX);
    dropdown.style.right = 'auto';

    if (placeAbove) {
      dropdown.style.top = 'auto';
      dropdown.style.bottom = `${window.innerHeight - triggerRect.top + 8}px`;
    } else {
      dropdown.style.bottom = 'auto';
      dropdown.style.top = `${triggerRect.bottom + 8}px`;
    }

    this.removeScrollListener();
    this.scrollListener = () => {
      if (this.isOpen) {
        this.positionPanel();
      }
    };
    window.addEventListener('scroll', this.scrollListener, true);
    window.addEventListener('resize', this.scrollListener, { passive: true });
  }

  private teardownPanel(): void {
    const el = this.panelDropdown?.nativeElement;
    if (el) {
      el.classList.remove('time-picker-panel-overlay');
      el.style.position = '';
      el.style.left = '';
      el.style.top = '';
      el.style.bottom = '';
      el.style.right = '';
      el.style.width = '';
      el.style.minWidth = '';
      el.style.maxWidth = '';
      el.style.zIndex = '';
      if (el.parentNode === document.body && this.dropdownOriginalParent) {
        this.dropdownOriginalParent.appendChild(el);
      }
    }
    this.dropdownOriginalParent = null;
    this.removeScrollListener();
  }

  private removeScrollListener(): void {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener, true);
      window.removeEventListener('resize', this.scrollListener);
      this.scrollListener = null;
    }
  }

  private getMinTimeMinutes(): number | null {
    const trimmed = String(this.minTime ?? '').trim();
    const match = /^(\d{1,2}):(\d{2})$/.exec(trimmed);
    if (!match) {
      return null;
    }
    const h = Number(match[1]);
    const m = Number(match[2]);
    if (!Number.isFinite(h) || !Number.isFinite(m) || h < 0 || h > 23 || m < 0 || m > 59) {
      return null;
    }
    return h * 60 + m;
  }

  private isSelectionBelowMinTime(): boolean {
    if (this.selectedHour === null || this.selectedMinute === null) {
      return false;
    }
    const minMinutes = this.getMinTimeMinutes();
    if (minMinutes === null) {
      return false;
    }
    const selected = this.selectedHour * 60 + this.selectedMinute;
    return selected < minMinutes;
  }

  private enforceMinTimeBound(): void {
    const minMinutes = this.getMinTimeMinutes();
    if (minMinutes === null) {
      return;
    }

    if (this.selectedHour === null || this.selectedMinute === null) {
      this.selectedHour = Math.floor(minMinutes / 60);
      this.selectedMinute = minMinutes % 60;
      return;
    }

    const selected = this.selectedHour * 60 + this.selectedMinute;
    if (selected >= minMinutes) {
      return;
    }

    this.selectedHour = Math.floor(minMinutes / 60);
    this.selectedMinute = minMinutes % 60;
  }

  private getTimeMinutesAfterHourDecrement(): number {
    const newHour = ((this.selectedHour ?? 0) + 23) % 24;
    const minute = this.selectedMinute ?? 0;
    return newHour * 60 + minute;
  }

  private getTimeMinutesAfterMinuteDecrement(): number {
    const step = this.getMinuteStep();
    const baseMinute = this.selectedMinute ?? 0;
    let nextMinute = baseMinute - step;
    let hour = this.selectedHour ?? 0;
    if (nextMinute < 0) {
      nextMinute = 60 - step;
      hour = (hour + 23) % 24;
    }
    return hour * 60 + nextMinute;
  }
}
